import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Award,
  User,
  Calendar,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Shield,
  FileText,
  ExternalLink
} from 'lucide-react';
import { api } from '@/utils/api';

interface CertificationView {
  id: number;
  user: {
    firstName: string;
    lastName: string;
    role: string;
  };
  certType: string;
  certName: string;
  certNumber: string | null;
  issueDate: string;
  expirationDate: string | null;
  documentUrl: string | null;
  daysUntilExpiration: number | null;
  isExpired: boolean;
}

export function CertShareViewPage() {
  const { token } = useParams<{ token: string }>();
  const [certs, setCerts] = useState<CertificationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  useEffect(() => {
    loadSharedCerts();
  }, [token]);

  const loadSharedCerts = async () => {
    try {
      const response = await api.get(`/certifications/share/${token}`);
      setCerts(response.data.data.certifications || []);
      setExpiresAt(response.data.data.expiresAt);
    } catch (error: any) {
      console.error('Error loading shared certifications:', error);
      setError(error.response?.data?.message || 'Failed to load certifications. Link may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (cert: CertificationView) => {
    if (!cert.expirationDate || cert.daysUntilExpiration === null) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-800 rounded-full">
          <CheckCircle className="h-4 w-4" />
          <span className="text-sm font-semibold">No Expiration</span>
        </div>
      );
    }

    if (cert.isExpired || cert.daysUntilExpiration < 0) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-800 rounded-full">
          <XCircle className="h-4 w-4" />
          <span className="text-sm font-semibold">EXPIRED</span>
        </div>
      );
    }

    if (cert.daysUntilExpiration <= 30) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-800 rounded-full">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm font-semibold">Expires in {cert.daysUntilExpiration} days</span>
        </div>
      );
    }

    if (cert.daysUntilExpiration <= 90) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm font-semibold">Expires in {cert.daysUntilExpiration} days</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full">
        <CheckCircle className="h-4 w-4" />
        <span className="text-sm font-semibold">Valid ({cert.daysUntilExpiration} days)</span>
      </div>
    );
  };

  // Group certs by user
  const certsByUser = certs.reduce((acc, cert) => {
    const key = `${cert.user.firstName} ${cert.user.lastName}`;
    if (!acc[key]) {
      acc[key] = {
        user: cert.user,
        certs: [],
      };
    }
    acc[key].certs.push(cert);
    return acc;
  }, {} as Record<string, { user: any; certs: CertificationView[] }>);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-green-600 mx-auto mb-4 animate-pulse" />
          <p className="text-lg text-gray-600">Loading certifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Link Unavailable</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
            <h4 className="font-semibold text-yellow-900 mb-2">Possible reasons:</h4>
            <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
              <li>The link has expired (links are valid for 24 hours)</li>
              <li>The link was typed incorrectly</li>
              <li>The link has been revoked</li>
            </ul>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Please contact the site foreman for a new link.
          </p>
        </div>
      </div>
    );
  }

  const totalCerts = certs.length;
  const expiredCerts = certs.filter(c => c.isExpired || (c.daysUntilExpiration !== null && c.daysUntilExpiration < 0)).length;
  const expiringCerts = certs.filter(c => c.daysUntilExpiration !== null && c.daysUntilExpiration >= 0 && c.daysUntilExpiration <= 90).length;
  const validCerts = certs.filter(c => c.daysUntilExpiration === null || c.daysUntilExpiration > 90).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-green-600 mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2 break-words">Crew Certifications</h1>
            <p className="text-lg text-gray-600">Shared via Conformara Crew Cert Share™</p>
          </div>

          {/* Expiration Notice */}
          {expiresAt && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-3">
                <Clock className="h-5 w-5 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  <strong>This link expires:</strong> {new Date(expiresAt).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Certifications</p>
                <p className="text-3xl font-bold text-gray-900">{totalCerts}</p>
              </div>
              <Award className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Valid</p>
                <p className="text-3xl font-bold text-green-600">{validCerts}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Expiring Soon</p>
                <p className="text-3xl font-bold text-yellow-600">{expiringCerts}</p>
              </div>
              <AlertTriangle className="h-12 w-12 text-yellow-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Expired</p>
                <p className="text-3xl font-bold text-red-600">{expiredCerts}</p>
              </div>
              <XCircle className="h-12 w-12 text-red-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Certifications by User */}
        <div className="space-y-6">
          {Object.entries(certsByUser).map(([userName, data]) => (
            <div key={userName} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              {/* User Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <User className="h-8 w-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{userName}</h2>
                    <p className="text-blue-100">{data.user.role}</p>
                    <p className="text-sm text-blue-100 mt-1">
                      {data.certs.length} certification{data.certs.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="p-6">
                <div className="space-y-4">
                  {data.certs.map((cert) => (
                    <div key={cert.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Award className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-semibold text-gray-900">{cert.certName}</h3>
                          </div>
                          <p className="text-sm text-gray-600">{cert.certType}</p>
                        </div>
                        {getStatusBadge(cert)}
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        {cert.certNumber && (
                          <div>
                            <label className="text-gray-600">Certificate Number</label>
                            <p className="font-medium text-gray-900">{cert.certNumber}</p>
                          </div>
                        )}
                        <div>
                          <label className="text-gray-600">Issue Date</label>
                          <div className="flex items-center gap-1 font-medium text-gray-900">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {new Date(cert.issueDate).toLocaleDateString()}
                          </div>
                        </div>
                        <div>
                          <label className="text-gray-600">Expiration Date</label>
                          <div className="flex items-center gap-1 font-medium text-gray-900">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {cert.expirationDate ? new Date(cert.expirationDate).toLocaleDateString() : 'No Expiration'}
                          </div>
                        </div>
                      </div>

                      {cert.documentUrl && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <a
                            href={cert.documentUrl.startsWith('/api/certifications/') ? `${api.defaults.baseURL}/certifications/share/${encodeURIComponent(token || '')}/documents/${cert.id}` : cert.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            <FileText className="h-4 w-4" />
                            View Certificate Document
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="bg-white rounded-lg shadow-md p-6 inline-block">
            <div className="flex items-center gap-3 text-gray-600">
              <Shield className="h-5 w-5 text-green-600" />
              <p className="text-sm">
                Powered by <strong className="text-gray-900">Conformara</strong> Crew Cert Share™
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This is a secure, temporary link. No login required.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

